import PropTypes from "prop-types";

/**
 * RealisticSwitchgear – High-fidelity 3D industrial switchgear panel component.
 * Features:
 * - 3 Phase status pilot lights (Red, Yellow, Green) scaling with load
 * - 3 Analog meters with needles strictly seated INSIDE the square meter dials (pivot cy=108, length 13px)
 * - 4 Feeder circuit breaker compartments with illuminated ON/OFF status flags scaling with value
 * - Digital microprocessor protection relay screen (integrated cleanly without clunky boxes)
 */
const RealisticSwitchgear = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const isTrip = statusColor === "red" && !isOffline;
  const val = typeof liveValue === "number" ? liveValue : (isRunning && !isOffline ? 100 : 0);

  // Power load ratio (0.0 to 1.0) strictly based on configured percentage ratio
  const powerRatio = isRunning && !isOffline ? (typeof ratio === "number" ? ratio : 1) : 0;

  // Active phases (0 to 3)
  const activePhases = powerRatio >= 0.66 ? 3 : powerRatio >= 0.33 ? 2 : powerRatio > 0 ? 1 : 0;

  // Number of active closed circuit breakers (0 to 4) scaling with value:
  // >= 75% -> 4 breakers closed (Green ON)
  // 50% - 74% -> 3 breakers closed
  // 25% - 49% -> 2 breakers closed
  // > 0% - 24% -> 1 breaker closed
  // 0% or offline -> 0 closed (all OFF / Trip)
  const activeBreakers = powerRatio >= 0.75 ? 4 : powerRatio >= 0.5 ? 3 : powerRatio >= 0.25 ? 2 : powerRatio > 0 ? 1 : 0;

  // Meter deflection angles (-30deg left to +30deg right) – contained within dial window
  const angle1 = activePhases >= 1 ? -30 + Math.min(1, powerRatio / 0.33) * 60 : -30;
  const angle2 = activePhases >= 2 ? -30 + Math.min(1, (powerRatio - 0.33) / 0.33) * 60 : -30;
  const angle3 = activePhases >= 3 ? -30 + Math.min(1, (powerRatio - 0.66) / 0.34) * 60 : -30;

  const breakerX = [76, 145, 227, 308];

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/switchgear.png"
        alt="SCADA Realistic Switchgear"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 390x472 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 390 472"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="swgLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── 3 Top Pilot Lights (Red, Yellow, Green) ── */}
        {/* Red / Phase R (cx: 71, cy: 60) */}
        <circle
          cx="71"
          cy="60"
          r="7"
          fill={activePhases >= 1 ? "#ef4444" : "#450a0a"}
          stroke={activePhases >= 1 ? "#fecaca" : "#292524"}
          strokeWidth="1.2"
          filter={activePhases >= 1 ? "url(#swgLedGlow)" : "none"}
        />
        {activePhases >= 1 && <circle cx="69.5" cy="58.5" r="2" fill="#ffffff" opacity="0.8" />}

        {/* Yellow / Phase Y (cx: 101, cy: 60) */}
        <circle
          cx="101"
          cy="60"
          r="7"
          fill={activePhases >= 2 ? "#facc15" : "#422006"}
          stroke={activePhases >= 2 ? "#fef08a" : "#292524"}
          strokeWidth="1.2"
          filter={activePhases >= 2 ? "url(#swgLedGlow)" : "none"}
        />
        {activePhases >= 2 && <circle cx="99.5" cy="58.5" r="2" fill="#ffffff" opacity="0.8" />}

        {/* Green / Phase B (cx: 131, cy: 60) */}
        <circle
          cx="131"
          cy="60"
          r="7"
          fill={activePhases >= 3 ? "#22c55e" : "#052e16"}
          stroke={activePhases >= 3 ? "#bbf7d0" : "#292524"}
          strokeWidth="1.2"
          filter={activePhases >= 3 ? "url(#swgLedGlow)" : "none"}
        />
        {activePhases >= 3 && <circle cx="129.5" cy="58.5" r="2" fill="#ffffff" opacity="0.8" />}

        {/* ── 3 Analog Meter Moving Needles (Strictly seated inside square meter dial window) ── */}
        {/* Meter 1 (cx: 69, cy: 108) */}
        <g style={{ transformOrigin: "69px 108px", transform: `rotate(${angle1}deg)` }}>
          <line x1="69" y1="108" x2="69" y2="95" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="69" cy="108" r="1.8" fill="#1e293b" stroke="#dc2626" strokeWidth="0.6" />
        </g>

        {/* Meter 2 (cx: 101, cy: 108) */}
        <g style={{ transformOrigin: "101px 108px", transform: `rotate(${angle2}deg)` }}>
          <line x1="101" y1="108" x2="101" y2="95" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="101" cy="108" r="1.8" fill="#1e293b" stroke="#dc2626" strokeWidth="0.6" />
        </g>

        {/* Meter 3 (cx: 133, cy: 108) */}
        <g style={{ transformOrigin: "133px 108px", transform: `rotate(${angle3}deg)` }}>
          <line x1="133" y1="108" x2="133" y2="95" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="133" cy="108" r="1.8" fill="#1e293b" stroke="#dc2626" strokeWidth="0.6" />
        </g>

        {/* ── Digital Relay Controller Display (Crisp LCD font directly on base screen) ── */}
        {isRunning && (
          <text
            x="102"
            y="166"
            fill="#0f172a"
            fontSize="9"
            fontWeight="bold"
            fontFamily="monospace"
            textAnchor="middle"
            letterSpacing="0.5"
          >
            {isTrip ? "TRIP" : `${Math.round(val)}A`}
          </text>
        )}

        {/* ── 4 Feeder Breaker Status Flags Scaling with Value (Green = ON, Red = OFF/Trip) ── */}
        {breakerX.map((xPos, idx) => {
          const isClosed = idx < activeBreakers && !isTrip && !isOffline;
          const isBreakerTrip = isTrip && idx === 0;

          return (
            <g key={idx}>
              <circle
                cx={xPos}
                cy="286"
                r="6.5"
                fill={isClosed ? "#22c55e" : isBreakerTrip ? "#ef4444" : "#475569"}
                stroke={isClosed ? "#86efac" : isBreakerTrip ? "#fca5a5" : "#1e293b"}
                strokeWidth="1"
                filter={isClosed || isBreakerTrip ? "url(#swgLedGlow)" : "none"}
                style={{
                  animation: isBreakerTrip
                    ? "scada-led-fast-blink 0.8s infinite"
                    : isClosed
                    ? "scada-led-blink 2.5s infinite ease-in-out"
                    : "none",
                }}
              />
              {isClosed && <circle cx={xPos - 1.8} cy="284.2" r="1.8" fill="#ffffff" opacity="0.8" />}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

RealisticSwitchgear.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticSwitchgear;
